<script setup lang="ts">
import { BillingInfo } from '@/@types'
import { Coupon } from '@/@types/cart'
import { Order, OrdersnapShot } from '@/@types/order'
import AdditionalInformation from '@/components/shared/admin/orders/AdditionalInformation.vue'
import Details from '@/components/shared/base_orders/show/Details.vue'
import StatusBadge from '@/components/shared/base_orders/show/StatusBadge.vue'
import Tickets from '@/components/shared/base_orders/show/Tickets.vue'
import BankSlip from '@/components/shared/checkout/BankSlip.vue'
import BillingCompany from '@/components/shared/checkout/BillingCompany.vue'
import CreditCard from '@/components/shared/checkout/CreditCard.vue'
import Paypal from '@/components/shared/checkout/Paypal.vue'
import Pix from '@/components/shared/checkout/Pix.vue'
import { Card, CardContent } from '@/components/ui/card'
import { Title } from '@/components/ui/title'
import { cartTotalsKeys, useCartTotals } from '@/composables/useCartTotals'
import { formatPrice } from '@/helpers/current-price'
import { pushGa4PurchaseEvent } from '@/helpers/ga4-ecommerce'
import LightLayout from '@/layouts/LightLayout.vue'
import OrderLayout from '@/layouts/OrderLayout.vue'
import { Head, Link } from '@inertiajs/vue3'
import { ArrowLeftCircle, CircleHelp } from 'lucide-vue-next'
import { computed, onMounted, provide, ref } from 'vue'
import { useI18n } from 'vue-i18n'

interface OrderWithCoupon extends Order {
    order_snap_shot: OrdersnapShot
    coupon: Coupon
}

const { order, show_order_details } = defineProps<{ order: OrderWithCoupon; show_order_details: string; billing_info: BillingInfo }>()

const { t } = useI18n()

const showPaymentComponent = ref(order?.status == 'pending')

const orderItems = computed(() => order.items)

const cartTotals = useCartTotals(orderItems)

provide(cartTotalsKeys, cartTotals)

onMounted(() => {
    const conversionValue = (order?.order_snap_shot?.total_cents ?? 0) / 100
    const shouldTrackConversion = order?.status === 'paid' && conversionValue > 0

    if (!shouldTrackConversion) return

    const transactionId = String(order.id)

    const gtagWindow = window as Window & {
        gtag?: (command: string, eventName: string, params: Record<string, unknown>) => void
    }

    if (typeof gtagWindow.gtag === 'function') {
        const conversionStorageKey = `google-ads-conversion-${transactionId}`

        if (!window.sessionStorage || !sessionStorage.getItem(conversionStorageKey)) {
            gtagWindow.gtag('event', 'conversion', {
                send_to: 'AW-1040042452/xNOJCNn-wrQZENST9-8D',
                value: Number(conversionValue.toFixed(2)),
                currency: 'BRL',
                transaction_id: transactionId,
            })

            if (window.sessionStorage) sessionStorage.setItem(conversionStorageKey, '1')
        }
    }

    const fbqWindow = window as Window & {
        fbq?: (trackType: string, eventName: string, params?: Record<string, unknown>, options?: Record<string, unknown>) => void
    }

    if (typeof fbqWindow.fbq === 'function') {
        const purchaseStorageKey = `meta-purchase-${transactionId}`
        if (!window.sessionStorage || !sessionStorage.getItem(purchaseStorageKey)) {
            fbqWindow.fbq('track', 'Purchase', {
                value: Number(conversionValue.toFixed(2)),
                currency: 'BRL',
            },
            {
                eventID: transactionId,
            })
        }

        if (window.sessionStorage) sessionStorage.setItem(purchaseStorageKey, '1')
    }

    const lintrkWindow = window as Window & {
        lintrk?: (trackType: string, params?: Record<string, unknown>) => void
    }

    if (typeof lintrkWindow.lintrk === 'function') {
        const purchaseStorageKey = `linkedin-purchase-${transactionId}`
        if (!window.sessionStorage || !sessionStorage.getItem(purchaseStorageKey)) {
            lintrkWindow.lintrk('track', {
                conversion_id: 18317433,
                conversion_value: Number(conversionValue.toFixed(2)),
                currency: 'BRL',
                event_id: transactionId,
            })
        }

        if (window.sessionStorage) sessionStorage.setItem(purchaseStorageKey, '1')
    }

    pushGa4PurchaseEvent(order, t)
})
</script>

<template>
    <Head :title="$t('orders.title')"></Head>

    <LightLayout contentClasses="bg-slate-50" :hideFooter="true" v-if="showPaymentComponent && !show_order_details">
        <BankSlip :order="order" v-if="order.payment_type == 'bank_slip'" />
        <BillingCompany :order="order" v-else-if="order.payment_type == 'company_billing'" />
        <CreditCard :order="order" v-else-if="order.payment_type == 'credit_card'" />
        <Pix :order="order" v-else-if="order.payment_type == 'pix'" />
        <Paypal :order="order" v-else-if="order.payment_type == 'paypal'" />
    </LightLayout>
    <OrderLayout v-else>

        <div class="flex flex-col gap-6">
            <Title class="flex items-center gap-2 font-sans text-3xl font-semibold text-white">
                <Link href="?show_details_order=false" v-if="show_order_details"><ArrowLeftCircle class="h-8 w-8" stroke-width="1.2" /></Link>
                {{ $t('View order') }}
            </Title>

            <div class="flex flex-col gap-6 lg:flex-row">
                <div class="flex flex-1 flex-col gap-6">
                    <Card class="border-transparent">
                        <CardContent class="pt-6">
                            <div class="flex flex-wrap justify-between gap-8">
                                <div class="space-y-2">
                                    <h2 class="font-sans text-2xl font-semibold text-gray-800">{{ $t('Order') }}: #{{ order.id }}</h2>
                                    <p class="text-slate-500">
                                        {{ $t('Order value') }}:
                                        <span class="text-foreground text-lg font-semibold">
                                            {{ formatPrice((order.order_snap_shot.total_cents ?? 0) / 100) }}
                                        </span>
                                    </p>
                                    <p class="text-slate-500">
                                        {{ $t('Discount') }}:
                                        <span class="text-foreground text-lg font-semibold">
                                            {{ formatPrice(order.order_snap_shot.discount_cents / 100) }}
                                        </span>
                                    </p>
                                </div>

                                <div class="w-full max-w-52">
                                    <StatusBadge :status="order.status" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Tickets :order="order" />

                    <AdditionalInformation :billing_info="billing_info" />

        
                    <footer class="flex items-center gap-2 rounded-sm bg-gray-100 px-6 py-4 text-sm">
                        <CircleHelp />
                        <p>
                            {{ $t('Need to cancel your order or ticket? Check out our') }}
                            <a href="/privacy-policy#cancellation" target="_blank" class="underline">{{ $t('cancellation policy') }}</a>
                        </p>
                    </footer>
                </div>
                <div class="w-full max-w-96">
                    <Details :order="order" />
                </div>
            </div>
        </div>
    </OrderLayout>
</template>
